import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SharedService } from '@shared/services/shared.service';
import { IEntityApplicant } from 'app/models/applicants/entity-applicant';
import { IStatus } from 'app/models/licenses/status';
import { EntityApplicantService } from 'app/services/applicant/entity-applicant.service';

@Component({
  selector: 'entity-applicant',
  templateUrl: './entity-applicant.component.html',
  styleUrls: ['./entity-applicant.component.scss']
})
export class EntityApplicantComponent implements OnInit, OnDestroy {
  @Input() entityId: number = 0;
  @Input() statuses: IStatus[] = [];
  @Output() applicantSelected: EventEmitter<boolean> = new EventEmitter<boolean>();

  public addingApplicant: boolean = false;
  public newApplicantCreated: boolean = false;
  public includeArchived: boolean = false;
  public applicant: IEntityApplicant = {
    id: 0,
    applicantId: '',
    entityName: '',
    businessInOregon: false,
    secretaryOfStateNumber: '',
    registeredBusiness: false,
    registeredIn: '',
    registryNumber: '',
    registrationAttestation: false,
    primaryContactName: '',
    primaryContactNumber: '',
    primaryContactEmail: '',
    archived: false
  }
  public applicants: IEntityApplicant[] = [];
  public applicantForm = new FormGroup({
    entityName: new FormControl('', Validators.required),
    primaryContactName: new FormControl('', Validators.required),
    primaryContactNumber: new FormControl('', Validators.required),
    primaryContactEmail: new FormControl('', [Validators.required, Validators.email]),
    businessInOregon: new FormControl(false),
    secretaryOfStateNumber: new FormControl(''),
    registeredBusiness: new FormControl(false),
    registeredIn: new FormControl(''),
    registryNumber: new FormControl('')
  });

  public applicantColumns: string [] = ['entityName', 'primaryContactName', 'archived', 'actions'];
  public applicantSource = new MatTableDataSource<IEntityApplicant>(this.applicants);
  @ViewChild('applicantPaginator', {static: false}) applicantPaginator: MatPaginator;
  @ViewChild('applicantSort', {static: false}) applicantSort: MatSort;

  public sharedService = inject(SharedService);
  public applicantService = inject(EntityApplicantService);

  ngOnInit(): void {
    this.getApplicants(this.includeArchived);
  }

  ngOnDestroy(){
    if(this.addingApplicant){
      this.saveApplicant();
    }
  }

  getApplicants(includeArchived: boolean): void {
    this.includeArchived = includeArchived;
    this.applicantService.getApplicants(this.entityId, includeArchived).subscribe({
      next: response => this.applicants = response,
      error: e => {
        this.sharedService.toastr.error("Could not get entity applicants");
        console.log('error getting entity applicants', e);
      },
      complete: () => this.updateApplicantTable()
    });
  }

  updateApplicantTable(): void {
    setTimeout(() => {
      this.applicantSource.data = this.applicants;
      this.applicantSource.sort = this.applicantSort;
      this.applicantSource.paginator = this.applicantPaginator;
    }, 500);
  }

  selectApplicant(applicant: IEntityApplicant): void {
    this.applicant = applicant;
    this.addingApplicant = true;
    this.updateApplicantForm(applicant);
    this.applicantSelected.emit(true);
  }

  toggleApplicantForm(adding: boolean): void {
    this.addingApplicant = adding;
    this.updateApplicantForm(this.getEmptyApplicant());
    if(!adding){
      if(this.newApplicantCreated){
        this.deleteApplicant();
      }
      else{
        this.updateApplicantTable();
        this.applicant = this.getEmptyApplicant();
      }
    }
    this.applicantSelected.emit(adding);
  }

  getEmptyApplicant(): IEntityApplicant {
    return {
      id: 0,
      applicantId: '',
      entityName: '',
      businessInOregon: false,
      secretaryOfStateNumber: '',
      registeredBusiness: false,
      registeredIn: '',
      registryNumber: '',
      registrationAttestation: false,
      primaryContactName: '',
      primaryContactNumber: '',
      primaryContactEmail: '',
      archived: false
    }
  }

  updateApplicantForm(applicant: IEntityApplicant): void {
    this.applicantForm.reset({
      entityName: applicant.entityName,
      businessInOregon: applicant.businessInOregon,
      secretaryOfStateNumber: applicant.secretaryOfStateNumber,
      registeredBusiness: applicant.registeredBusiness,
      registeredIn: applicant.registeredIn,
      registryNumber: applicant.registryNumber,
      primaryContactName: applicant.primaryContactName,
      primaryContactNumber: applicant.primaryContactNumber,
      primaryContactEmail: applicant.primaryContactEmail
    });
  }

  updateApplicant(): void {
    let form = this.applicantForm.value;
    this.applicant.entityName = form.entityName,
    this.applicant.businessInOregon = form.businessInOregon,
    this.applicant.secretaryOfStateNumber = form.secretaryOfStateNumber,
    this.applicant.registeredBusiness = form.registeredBusiness,
    this.applicant.registeredIn = form.registeredIn,
    this.applicant.registryNumber = form.registryNumber,
    this.applicant.primaryContactName = form.primaryContactName,
    this.applicant.primaryContactNumber = form.primaryContactNumber,
    this.applicant.primaryContactEmail = form.primaryContactEmail
  }

  createApplicant(): void {
    this.applicantService.createApplicant(this.applicant, this.entityId).subscribe({
      next: response => {
        this.applicants.push(response);
        this.applicant = response;
      },
      error: e => {
        this.sharedService.toastr.error("Error Creating Applicant");
        console.log('error creating entity applicant', e);
      },
      complete: () => {
        this.toggleApplicantForm(true);
        this.newApplicantCreated = true;
      }
    });
  }

  saveApplicant(): void {
    this.updateApplicant();
    this.applicantService.updateApplicant(this.applicant).subscribe({
      complete: () => {
        this.sharedService.toastr.success("Updated the Applicant");
        this.newApplicantCreated = false;
      },
      error: e => {
        this.sharedService.toastr.error("Could Not Save Applicant");
        console.log('error updating entity applicant', e);
      }
    });
  }

  archiveApplicant(applicant: IEntityApplicant, archive: boolean): void {
    let archiving: string = archive? 'Archive ' : 'Un-Archive ';
    this.sharedService.openConfirm(archiving + 'this entity applicant?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.applicantService.archiveApplicant(applicant.id, archive).subscribe({
            complete: () => {
              applicant.archived = archive;
              if(archive){
                this.applicants = this.applicants.filter(a => a.id !== applicant.id);
              }
              this.toggleApplicantForm(false);
            },
            error: e => {
              this.sharedService.toastr.error("Could Not Archive Applicant");
              console.log('error archiving entity applicant', e);
            }
          });
        }
      }
    );
  }

  deleteApplicant(): void {
    this.applicantService.deleteApplicant(this.applicant.id).subscribe({
      complete: () => {
        this.newApplicantCreated = false;
        this.applicants = this.applicants.filter(a => a.id !== this.applicant.id);
        this.updateApplicantTable();
        this.applicant = this.getEmptyApplicant();
      },
      error: e => console.log('error deleting applicant', e)
    });
  }

  public get locked(): boolean {
    return this.statuses.some(s => s.label === this.sharedService.submitted);
  }

}
